<div class="settings-page categories-page">
  <div class="settings-topbar">
    <div class="topbar-left">
      <h1>
        Categories
        <small>Inventory product categories</small>
      </h1>
      <div class="breadcrumb">
        <i class="bi bi-house-door"></i>
        Settings / <span class="current">Categories</span>
      </div>
    </div>
    <div class="topbar-right">
      <button type="button" class="btn btn-primary" (click)="openModal()">
        <i class="bi bi-plus-circle"></i> Add Category
      </button>
    </div>
  </div>

  <div class="settings-table-wrap">
    <div class="table-scroll">
      <table class="report-data-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (row of categories(); track row.id) {
            <tr>
              <td><span class="fw-semibold">{{ row.code }}</span></td>
              <td>{{ row.name }}</td>
              <td>
                <span class="category-description" [class.text-muted]="!row.description">
                  {{ row.description || '—' }}
                </span>
              </td>
              <td>
                <span class="status-pill" [class.active]="row.status === 'active'">
                  {{ row.status }}
                </span>
              </td>
              <td class="actions-cell">
                <div class="row-actions">
                  <button type="button" class="btn-action edit" title="Edit category" (click)="openEdit(row)">
                    <i class="bi bi-pencil"></i>
                  </button>
                  @if (auth.hasPermission('delete_inventory')) {
                  <button type="button" class="btn-action delete" title="Delete category" (click)="deleteRow(row)">
                    <i class="bi bi-trash"></i>
                  </button>
                  }
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="5" class="text-center py-4 text-muted">
                <i class="bi bi-inbox empty-state-icon"></i>
                No categories found. Click "Add Category" to create one.
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
</div>

@if (showModal()) {
  <div class="modal-overlay open" (click)="closeModal()">
    <div class="settings-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="bi" [class]="isEditing() ? 'bi-pencil' : 'bi-plus-circle'"></i>
          {{ isEditing() ? 'Edit Category' : 'Add Category' }}
        </h2>
        <button type="button" class="close-btn" (click)="closeModal()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">Code</label>
          @if (isEditing() && categoryForm.code) {
            <input type="text" class="form-control" [value]="categoryForm.code" disabled />
            <small class="text-muted">Generated automatically and cannot be changed.</small>
          } @else {
            <input type="text" class="form-control" value="Auto-generated on save" disabled />
            <small class="text-muted">A unique code is created for your branch when you save.</small>
          }
        </div>
        <div class="form-group">
          <label class="form-label">Name <span class="text-danger">*</span></label>
          <input type="text" class="form-control" [(ngModel)]="categoryForm.name" placeholder="Enter category name" />
        </div>
        <div class="form-group">
          <label class="form-label">Description</label>
          <textarea
            class="form-control"
            [(ngModel)]="categoryForm.description"
            placeholder="Enter category description"
            rows="3"
          ></textarea>
        </div>
        <div class="form-group">
          <label class="form-label">Status</label>
          <select class="form-select" [(ngModel)]="categoryForm.status">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeModal()">
          <i class="bi bi-x-lg"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" (click)="save()">
          <i class="bi bi-check-lg"></i> {{ isEditing() ? 'Save Changes' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
}

@if (showDeleteModal()) {
  <div class="modal-overlay open" (click)="closeDeleteModal()">
    <div class="settings-modal delete-impact-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="bi bi-trash"></i>
          Delete Category
        </h2>
        <button type="button" class="close-btn" (click)="closeDeleteModal()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="modal-body">
        @if (deleteLoading()) {
          <p class="text-muted mb-0">Checking for items in this category…</p>
        } @else {
          @if (deleteImpact(); as impact) {
            <p class="delete-lead">
              Delete <strong>{{ deleteTarget()?.name }}</strong>?
            </p>

            @if (impact.requiresReassignment) {
              <div class="impact-banner">
                <i class="bi bi-exclamation-triangle-fill"></i>
                <div>
                  <strong>This category still has items.</strong>
                  <span>
                    {{ impact.itemCount }} item(s)
                    @if (impact.activeItemCount !== impact.itemCount) {
                      ({{ impact.activeItemCount }} active)
                    }
                    must be moved to another category before delete.
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">
                  Move items to category <span class="text-danger">*</span>
                </label>
                <select class="form-select" [(ngModel)]="targetItemCategoryId">
                  <option value="">Select destination category</option>
                  @for (cat of destinationCategories(); track cat.id) {
                    <option [value]="cat.id">{{ cat.name }}</option>
                  }
                </select>
                @if (!destinationCategories().length) {
                  <small class="text-danger">
                    Add another active category first.
                  </small>
                }
              </div>

              @if (impact.items.length) {
                <div class="impact-items">
                  <div class="impact-items-title">Items to move</div>
                  <ul>
                    @for (item of impact.items.slice(0, 8); track item.inventoryItemId) {
                      <li>
                        <span>{{ item.itemName }}</span>
                        <span class="qty">{{ item.itemCode || '—' }}</span>
                      </li>
                    }
                    @if (impact.items.length > 8) {
                      <li class="more">+{{ impact.items.length - 8 }} more…</li>
                    }
                  </ul>
                </div>
              }
            } @else {
              <p class="text-muted mb-0">
                No items are linked to this category. It can be deleted safely.
              </p>
            }
          }
        }

        @if (deleteError()) {
          <div class="delete-error">{{ deleteError() }}</div>
        }
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeDeleteModal()" [disabled]="deleteSubmitting()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="confirmDelete()"
          [disabled]="deleteLoading() || deleteSubmitting() || !deleteImpact() || (deleteImpact()!.requiresReassignment && !destinationCategories().length)"
        >
          <i class="bi bi-trash"></i>
          {{ deleteSubmitting() ? 'Deleting…' : 'Delete Category' }}
        </button>
      </div>
    </div>
  </div>
}

@if (showToast()) {
  <div class="toast-container show">
    <div class="toast" [class.success]="!toastIsError()" [class.error]="toastIsError()">
      <div class="toast-icon">
        <i class="bi" [class]="toastIsError() ? 'bi-exclamation-circle-fill' : 'bi-check-circle-fill'"></i>
      </div>
      <div class="toast-content">
        <div class="toast-title">{{ toastIsError() ? 'Unable to save' : 'Success!' }}</div>
        <div class="toast-message">{{ toastMessage() }}</div>
      </div>
    </div>
  </div>
}
